import { metadataHook } from '@hooks/metadata';
import { createContext, useContext, useMemo, useState } from 'react';
import { PermissionPageKey, Permissions } from '@utility/constants/permission';

import { sidebarMenus } from '@hooks/sidebar';
import { buildPermissionIndex, resolveMenuByUrl } from '@hooks/menu-permission';
import { useAuth } from './auth';
import { exportSheetTypes } from '@utility/constants/types';
import { PopupWorkspace } from '@layouts/components/WorkspacePopup';
import { useSidebarCount } from '@layouts/Sidebar/use-sidebar-count';

// @ts-expect-error - TS2554: Expected 1 arguments, but got 0.
const SidebarContext = createContext();

function getSCMPermission(modulePermission) {
  let permission = Permissions.hidden;
  let permissionKey = PermissionPageKey.Dashboard;
  Object.keys(modulePermission.modules || {}).forEach(key => {
    if (permission !== Permissions.hidden) {
      return;
    }
    permissionKey = key;
    if (key === PermissionPageKey.SP) {
      permission = modulePermission.modules[key]?.permission;
    } else {
      permission = modulePermission.modules[key];
    }
  });

  return {
    permission,
    permissionKey,
  };
}

export function SidebarProvider({ children }) {
  const { metadata } = metadataHook();
  const { user, isAuthenticated, wmsPortal, currentRoute: asPath } = useAuth();
  const { data, loading, refreshSidebar } = useSidebarCount(
    isAuthenticated,
    wmsPortal,
  );
  // in some cases, we may need to hide the sidebar
  const [showSidebar, setShowSidebar] = useState(true);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  // below the lg breakpoint the sidebar is a drawer; this is whether it is out
  const [isNavOpen, setIsNavOpen] = useState(false);

  const toggleSidebarVisibility = (showSidebar = true) => {
    setShowSidebar(showSidebar);
  };

  const [isOnboardingFooterVisible, setIsOnboardingFooterVisible] =
    useState(false);

  const { menus, permissionIndex } = useMemo(() => {
    if (
      // @ts-expect-error - TS2339: Property 'orderStatus' does not exist on type '{}'.
      Object.keys(metadata?.orderStatus || {}).length === 0 ||
      !isAuthenticated ||
      Object.keys(user?.permissions || {}).length === 0
    ) {
      return {
        menus: [],
        permissionIndex: buildPermissionIndex([]),
      };
    }

    // @ts-expect-error - TS18047: 'user' is possibly 'null'.
    const scmMenu = getSCMPermission(user.permissions[PermissionPageKey.SCM]);

    const wholeSalePermission =
      // @ts-expect-error - TS18047: 'user' is possibly 'null'.
      user.permissions[PermissionPageKey.Wholesale]?.permission;
    const catalogPermission =
      // @ts-expect-error - TS18047: 'user' is possibly 'null'.
      user.permissions[PermissionPageKey.Catalog].permission;

    const menus = sidebarMenus(
      user,
      metadata,
      data,
      scmMenu,
      catalogPermission,
      wholeSalePermission,
    );

    return { menus, permissionIndex: buildPermissionIndex(menus) };
  }, [metadata, data, user, isAuthenticated]);

  const getMenuByUrl = (url, options?) =>
    resolveMenuByUrl(permissionIndex, user, url, options);

  const currentMenuPermission = useMemo(
    () => getMenuByUrl(asPath)?.permission,
    [asPath, permissionIndex],
  );

  const openExportPopup = (sheetName = exportSheetTypes.purchaseOrder) => {
    // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
    setShowExportPopup(sheetName);
  };

  const closeExportPopup = () => {
    // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'SetStateAction<b...
    setShowExportPopup(null);
  };

  return (
    <>
      <SidebarContext.Provider
        value={{
          data,
          refreshSidebar,
          loading,
          menus,
          getMenuByUrl,
          currentMenuPermission,
          isOnboardingFooterVisible,
          setIsOnboardingFooterVisible,
          toggleSidebarVisibility,
          showSidebar,
          showExportPopup,
          openExportPopup,
          closeExportPopup,
          showWorkspace,
          setShowWorkspace,
          isNavOpen,
          setIsNavOpen,
        }}
      >
        {children}
      </SidebarContext.Provider>
      <PopupWorkspace
        open={showWorkspace}
        closePopup={() => setShowWorkspace(false)}
      />
    </>
  );
}

export const useSidebar = () => useContext(SidebarContext);

const noRefresh = () => Promise.resolve();

/* Call after a write that changes a sidebar count: the next answer skips the
 * API's 60 s cache. Outside the provider it does nothing. */
export const useRefreshSidebar = (): (() => Promise<void>) =>
  (
    useContext(SidebarContext) as
      | { refreshSidebar?: () => Promise<void> }
      | undefined
  )?.refreshSidebar ?? noRefresh;
